import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import {Menu, X, User, LogOut, ChevronDown, QrCode} from 'lucide-react';
import QRCode from 'react-qr-code';
import { lumi } from "./lib/lumi";
import HomePage from "./pages/HomePage";
import AboutM91 from "./pages/AboutM91";
import Executives from "./pages/Executives";
import Gallery from "./pages/Gallery";
import Projects from "./pages/Projects";
import Members from "./pages/Members";
import AboutMfantsipim from "./pages/AboutMfantsipim";
import ContactUs from "./pages/ContactUs";
import HotelReservation from "./pages/HotelReservation";
import Store from "./pages/Store";
import { Toaster } from "react-hot-toast";

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(lumi.auth.user);

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user);
    });
    return unsubscribe;
  }, []);

  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn();
    } catch (error) {
      console.error("Sign in failed:", error);
    }
  };

  const handleSignOut = () => {
    lumi.auth.signOut();
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: { background: "#0A0A0A", color: "#fff", border: "1px solid #DC143C" },
          success: { style: { background: "#DC143C" } },
          error: { style: { background: "#FF4444" } }
        }} />

      <Router>
        <div className="min-h-screen bg-white text-black">
          {/* Anniversary Ticker */}
          <AnniversaryTicker />

          {/* Navigation */}
          <Navigation
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            user={user}
            handleSignIn={handleSignIn}
            handleSignOut={handleSignOut} />


          {/* Routes */}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutM91 />} />
            <Route path="/executives" element={<Executives />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/members" element={<Members />} />
            <Route path="/about-mfantsipim" element={<AboutMfantsipim />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/hotel-reservation" element={<HotelReservation />} />
            <Route path="/store" element={<Store />} />
          </Routes>

          {/* Footer */}
          <Footer />
          
          {/* QR Code Button */}
          <QRCodeButton />
        </div>
      </Router>
    </>);

}

function AnniversaryTicker() {
  return (
    <div className="bg-gradient-to-r from-[#DC143C] via-red-600 to-[#DC143C] py-2 overflow-hidden">
      <div className="animate-marquee whitespace-nowrap">
        <span className="text-sm font-semibold mx-8">
          🎉 MOBA91 40th Anniversary - September 12, 2026
        </span>
        <span className="text-sm font-semibold mx-8">
          🏫 Mfantsipim School 150th Anniversary - November 2026
        </span>
        <span className="text-sm font-semibold mx-8">
          🎉 MOBA91 40th Anniversary - September 12, 2026
        </span>
        <span className="text-sm font-semibold mx-8">
          🏫 Mfantsipim School 150th Anniversary - November 2026
        </span>
      </div>
    </div>);

}

function Navigation({ mobileMenuOpen, setMobileMenuOpen, user, handleSignIn, handleSignOut }: any) {
  const location = useLocation();
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);

  const navLinks = [
  { name: "Home", path: "/" },
  { name: "Gallery", path: "/gallery" },
  { name: "Events", path: "/projects" },
  { name: "Store", path: "/store" },
  { name: "Hotel", path: "/hotel-reservation" },
  { name: "Contact", path: "/contact" }];

  const aboutSubLinks = [
  { name: "About Us", path: "/about-us" },
  { name: "Executives", path: "/executives" },
  { name: "Members", path: "/members" },
  { name: "About Mfantsipim", path: "/about-mfantsipim" }];


  const isActive = (path: string) => location.pathname === path;
  const isAboutActive = aboutSubLinks.some((link) => location.pathname === link.path);

  return (
    <nav className="bg-white border-b border-[#DC143C]/20 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg"></span>
            </div>
            <div className="hidden sm:block">
              <div className="text-xl font-bold text-black">MOBA 91</div>
              <div className="text-xs text-gray-600">Alumni Association</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.slice(0, 1).map((link) =>
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              isActive(link.path) ?
              "bg-[#DC143C] text-white" :
              "text-gray-300 hover:bg-[#1A1A1A] hover:text-white"}`
              }>

                {link.name}
              </Link>
            )}
            
            {/* About Us Dropdown */}
            <div className="relative">
              <button
                onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-1 ${
                isAboutActive ?
                "bg-[#DC143C] text-white" :
                "text-gray-700 hover:bg-gray-100 hover:text-black"}`
                }>
                <span>About</span>
                <ChevronDown size={16} className={`transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {aboutDropdownOpen &&
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-[#DC143C]/30 rounded-lg shadow-xl py-2">
                  {aboutSubLinks.map((subLink) =>
                <Link
                  key={subLink.path}
                  to={subLink.path}
                  onClick={() => setAboutDropdownOpen(false)}
                  className={`block px-4 py-2 text-sm transition-all ${
                  isActive(subLink.path) ?
                  "bg-[#DC143C] text-white" :
                  "text-gray-700 hover:bg-gray-100 hover:text-black"}`
                  }>
                      {subLink.name}
                    </Link>
                )}
                </div>
              }
            </div>

            {navLinks.slice(1).map((link) =>
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              isActive(link.path) ?
              "bg-[#DC143C] text-white" :
              "text-gray-700 hover:bg-gray-100 hover:text-black"}`
              }>

                {link.name}
              </Link>
            )}
          </div>

          {/* Auth Button */}
          <div className="hidden lg:flex items-center space-x-4">
            {user ?
            <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-black">{user.userName}</div>
                  <div className="text-xs text-gray-600">{user.userRole}</div>
                </div>
                <button
                onClick={handleSignOut}
                className="p-2 rounded-lg bg-gray-100 hover:bg-[#DC143C] hover:text-white transition-all">

                  <LogOut size={20} />
                </button>
              </div> :

            <button
              onClick={handleSignIn}
              className="px-6 py-2 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all flex items-center space-x-2">

                <User size={18} />
                <span>Member Sign In</span>
              </button>
            }
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-[#DC143C] hover:text-white transition-all text-black">

            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen &&
      <div className="lg:hidden bg-white border-t border-[#DC143C]/20">
          <div className="px-4 py-4 space-y-2">
            <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
            isActive("/") ?
            "bg-[#DC143C] text-white" :
            "text-gray-700 hover:bg-gray-100 hover:text-black"}`
            }>
              Home
            </Link>
            
            {/* About Us Section */}
            <div className="space-y-1">
              <div className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase">About Us</div>
              {aboutSubLinks.map((subLink) =>
            <Link
              key={subLink.path}
              to={subLink.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              isActive(subLink.path) ?
              "bg-[#DC143C] text-white" :
              "text-gray-700 hover:bg-gray-100 hover:text-black"}`
              }>
                  {subLink.name}
                </Link>
            )}
            </div>

            {navLinks.slice(1).map((link) =>
          <Link
            key={link.path}
            to={link.path}
            onClick={() => setMobileMenuOpen(false)}
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
            isActive(link.path) ?
            "bg-[#DC143C] text-white" :
            "text-gray-700 hover:bg-gray-100 hover:text-black"}`
            }>

                {link.name}
              </Link>
          )}
            <div className="pt-4 border-t border-[#DC143C]/20">
              {user ?
            <div>
                  <div className="px-4 py-2 text-sm">
                    <div className="font-medium text-black">{user.userName}</div>
                    <div className="text-xs text-gray-600">{user.userRole}</div>
                  </div>
                  <button
                onClick={handleSignOut}
                className="w-full px-4 py-3 bg-gray-100 hover:bg-[#DC143C] hover:text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 text-black">

                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </div> :

            <button
              onClick={handleSignIn}
              className="w-full px-4 py-3 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2">

                  <User size={18} />
                  <span>Member Sign In</span>
                </button>
            }
            </div>
          </div>
        </div>
      }
    </nav>);

}

function QRCodeButton() {
  const [showQR, setShowQR] = useState(false);
  const websiteUrl = window.location.origin;

  return (
    <>
      {/* Floating QR Code Button */}
      <button
        onClick={() => setShowQR(true)}
        className="fixed bottom-6 right-6 bg-[#DC143C] hover:bg-red-600 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110 z-40"
        title="Show QR Code">

        <QrCode size={24} />
      </button>

      {/* QR Code Modal */}
      {showQR &&
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={() => setShowQR(false)}>

          <div
          className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}>

            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-black">Scan QR Code</h3>
              <button
              onClick={() => setShowQR(false)}
              className="text-gray-500 hover:text-black transition-colors">

                <X size={24} />
              </button>
            </div>
            
            <div className="bg-white p-6 rounded-xl border-2 border-[#DC143C]/20 mb-4">
              <QRCode
              value={websiteUrl}
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox={`0 0 256 256`}
              fgColor="#000000"
              bgColor="#FFFFFF" />

            </div>
            
            <p className="text-center text-sm text-gray-600 mb-2">
              Scan to visit MOBA91 Website
            </p>
            <p className="text-center text-xs text-gray-500 font-mono break-all">
              {websiteUrl}
            </p>
          </div>
        </div>
      }
    </>);

}

function Footer() {
  return (
    <footer className="bg-gray-50 border-t border-[#DC143C]/30 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-gray-700 text-sm">© 2026 MOBA 91 Alumni Association. All rights reserved.

        </div>
        <div className="text-gray-600 text-xs mt-2">
          Mfantsipim School, Cape Coast, Ghana
        </div>
      </div>
    </footer>);

}

export default App;